import { ToolSchema, ToolImplementation, ToolResult } from '../tool-types';

// Valid eVB characters: 0-9 and A-Z excluding I and O
const EVB_CHARS = '0123456789ABCDEFGHJKLMNPQRSTUVWXYZ';
const EVB_PREFIX = 'PV';

function generateEvbNumber(): string {
  let suffix = '';
  for (let i = 0; i < 5; i++) {
    suffix += EVB_CHARS[Math.floor(Math.random() * EVB_CHARS.length)];
  }
  return `${EVB_PREFIX}${suffix}`;
}

// --- issue_evb ---

export const issueEvbSchema: ToolSchema = {
  type: 'function',
  name: 'issue_evb',
  description:
    'Issue a new eVB-Nummer (elektronische Versicherungsbestätigung) for vehicle registration. ' +
    'Returns a 7-character alphanumeric code.',
  parameters: {
    type: 'object',
    properties: {
      coverage_type: {
        type: 'string',
        enum: ['haftpflicht', 'teilkasko', 'vollkasko'],
        description: 'Insurance coverage type'
      },
      start_date: {
        type: 'string',
        description: 'Desired insurance start date (e.g. "2026-03-05" or "sofort"). Optional.'
      }
    },
    required: ['coverage_type'],
    additionalProperties: false
  }
};

export const issueEvbImplementation: ToolImplementation = async (
  args: any
): Promise<ToolResult> => {
  const { coverage_type, start_date } = args ?? {};

  if (!coverage_type) {
    return { success: false, error: 'coverage_type is required.' };
  }

  const evbNumber = generateEvbNumber();
  const coverageLabel: Record<string, string> = {
    haftpflicht: 'Kfz-Haftpflicht',
    teilkasko: 'Haftpflicht + Teilkasko',
    vollkasko: 'Haftpflicht + Vollkasko'
  };

  return {
    success: true,
    data: {
      evb_number: evbNumber,
      coverage: coverageLabel[coverage_type] ?? coverage_type,
      start_date: start_date ?? 'sofort',
      valid_for_months: 6,
      delivery: 'SMS und E-Mail'
    }
  };
};

// --- lookup_evb ---

export const lookupEvbSchema: ToolSchema = {
  type: 'function',
  name: 'lookup_evb',
  description:
    'Look up the status of an existing eVB-Nummer. ' +
    'Returns the current status (active, not_activated, expired, unknown).',
  parameters: {
    type: 'object',
    properties: {
      evb_number: {
        type: 'string',
        description: 'The 7-character eVB-Nummer to look up'
      }
    },
    required: ['evb_number'],
    additionalProperties: false
  }
};

const SIMULATED_STATUSES = [
  {
    status: 'not_activated',
    reason: 'Die eVB wurde für eine frühere Anfrage reserviert, aber nicht final aktiviert.',
    action: 'Stornierung und Neuausstellung empfohlen.'
  },
  {
    status: 'expired',
    reason: 'Die eVB ist abgelaufen (älter als 6 Monate ohne Zulassung).',
    action: 'Neuausstellung erforderlich.'
  }
];

export const lookupEvbImplementation: ToolImplementation = async (
  args: any
): Promise<ToolResult> => {
  const { evb_number } = args ?? {};

  if (!evb_number || typeof evb_number !== 'string') {
    return { success: false, error: 'evb_number is required.' };
  }

  // For demo purposes: always return a "problem" status so the
  // agent can demonstrate the resolution flow.
  const simulated = SIMULATED_STATUSES[
    Math.floor(Math.random() * SIMULATED_STATUSES.length)
  ];

  return {
    success: true,
    data: {
      evb_number,
      ...simulated
    }
  };
};

// --- calculate_premium ---

export const calculatePremiumSchema: ToolSchema = {
  type: 'function',
  name: 'calculate_premium',
  description:
    'Calculate the premium difference when a customer changes vehicles on an existing policy. ' +
    'Returns the monthly and one-time difference.',
  parameters: {
    type: 'object',
    properties: {
      old_vehicle_description: {
        type: 'string',
        description: 'Description of the old vehicle (e.g. "VW Golf 2019")'
      },
      new_vehicle_description: {
        type: 'string',
        description: 'Description of the new vehicle (e.g. "VW Passat 2023")'
      },
      coverage_type: {
        type: 'string',
        enum: ['haftpflicht', 'teilkasko', 'vollkasko'],
        description: 'Insurance coverage type'
      }
    },
    required: ['old_vehicle_description', 'new_vehicle_description', 'coverage_type'],
    additionalProperties: false
  }
};

export const calculatePremiumImplementation: ToolImplementation = async (
  args: any
): Promise<ToolResult> => {
  const { old_vehicle_description, new_vehicle_description, coverage_type } = args ?? {};

  if (!old_vehicle_description || !new_vehicle_description || !coverage_type) {
    return { success: false, error: 'All parameters are required.' };
  }

  // Simulate a small premium difference for the demo
  const monthlyDiff = (Math.floor(Math.random() * 8) + 2) * 5; // 10–50 EUR range
  const isIncrease = Math.random() > 0.3; // 70% chance of increase for realism

  return {
    success: true,
    data: {
      old_vehicle: old_vehicle_description,
      new_vehicle: new_vehicle_description,
      coverage: coverage_type,
      monthly_difference_eur: isIncrease ? monthlyDiff : -monthlyDiff,
      direction: isIncrease ? 'Nachzahlung' : 'Gutschrift',
      one_time_adjustment_eur: isIncrease ? monthlyDiff : -monthlyDiff,
      note: isIncrease
        ? `Einmalige Nachzahlung von ${monthlyDiff} € für den laufenden Monat.`
        : `Einmalige Gutschrift von ${monthlyDiff} € für den laufenden Monat.`
    }
  };
};
